import { safeRemoveItem, safeSetItem } from "@/utils/safeStorage";

import { withMixpanel } from "../events/mixpanelClient";

/**
 * Sign-up bonus A/B test (backend ADR 0027) — a 3 vs 5 credit sign-on bonus.
 * Enrolled once, when the backend mints the account, which is what keeps it
 * disjoint from the closed daily test: an account that predates this test is
 * never in it and keeps the flat bonus. Read off `user.experimentGroups`,
 * reported as a super property plus the localStorage mirror that feeds
 * `x-mp-test-groups`.
 *
 * The backend maps this cohort itself, but only onto the profile. The header
 * is what puts the arm on server-side events as an event property, so the
 * mirror is not redundant.
 *
 */

/** localStorage mirror key and Mixpanel property name, one and the same. */
export const SIGNUP_BONUS_TEST_GROUP_ID = "signup_bonus_ab";
export const SIGNUP_BONUS_TEST_MP_PROPERTY = SIGNUP_BONUS_TEST_GROUP_ID;

export type SignupBonusGroup = "signup-bonus-3" | "signup-bonus-5";

// Identity, on purpose: the backend reports the raw cohort as the profile
// value, and a differently spelled arm would split every report.
const GROUP_BY_COHORT: Record<string, SignupBonusGroup> = {
  "signup-bonus-3": "signup-bonus-3",
  "signup-bonus-5": "signup-bonus-5",
};

/** The arm a profile's experiment groups put the user in, or null. */
export const signupBonusGroupFromProfile = (
  experimentGroups: readonly string[] | undefined,
): SignupBonusGroup | null => {
  for (const cohort of experimentGroups ?? []) {
    const group = GROUP_BY_COHORT[cohort];
    if (group) return group;
  }
  return null;
};

/**
 * Mirrors the arm from a fresh `/profile` into Mixpanel and localStorage.
 * Re-registered every call because `mixpanel.reset()` on logout wipes it.
 */
export const reportSignupBonusTestGroup = (
  experimentGroups: readonly string[] | undefined,
): void => {
  const group = signupBonusGroupFromProfile(experimentGroups);
  if (!group) return;
  withMixpanel((mp) => mp.register({ [SIGNUP_BONUS_TEST_MP_PROPERTY]: group }));
  safeSetItem(SIGNUP_BONUS_TEST_GROUP_ID, group);
};

/**
 * Drops the mirror when a session ends. Per-user server state: left behind it
 * rides the header into the next account and is `$set_once` onto the wrong
 * profile, permanently.
 */
export const clearSignupBonusTestGroup = (): void =>
  safeRemoveItem(SIGNUP_BONUS_TEST_GROUP_ID);
